import { useState, useEffect, useCallback } from 'react';
import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { navigate } from 'gatsby';
import { deleteHeaders } from '@agria/paws/src/utils/updateHeaders';
import { myAccountLinks } from '../const/myAccountLinks';
import { myAccountConfig } from '../const/myAccountConfig';
import { User } from '../types/types';

type AuthType = 'customer' | 'partner';

interface AuthState {
  currentAuthType: AuthType;
  isUserAuthenticated: boolean;
}

interface AuthManager extends AuthState {
  loginAsCustomer: () => Promise<void>;
  loginAsPartner: () => Promise<void>;
  logout: () => void;
  clearState: () => void;
  getCurrentInstance: () => PublicClientApplication;
  checkAuthentication: () => boolean;
}

/**
 * Manages dual authentication flow for customer and partner MSAL instances
 * Handles auth type detection, login flows, and session management
 */
export const useAuthManager = (
  customerInstance: PublicClientApplication,
  partnerInstance: PublicClientApplication,
  user: User | null,
  setUser: (user: User | null) => void
): AuthManager => {
  const [currentAuthType, setCurrentAuthType] = useState<AuthType>('customer');
  const [isUserAuthenticated, setIsUserAuthenticated] =
    useState<boolean>(false);

  // Authentication utilities
  const checkAuthentication = useCallback(() => {
    const customerAccounts = customerInstance.getAllAccounts();
    const partnerAccounts = partnerInstance.getAllAccounts();
    return customerAccounts.length > 0 || partnerAccounts.length > 0;
  }, [customerInstance, partnerInstance]);

  const getCurrentInstance = useCallback(() => {
    const customerAccounts = customerInstance.getAllAccounts();
    const partnerAccounts = partnerInstance.getAllAccounts();

    // Check which instance actually has active accounts first
    // This is more reliable than relying on currentAuthType state
    if (partnerAccounts.length > 0 && customerAccounts.length === 0) {
      return partnerInstance;
    }

    if (customerAccounts.length > 0 && partnerAccounts.length === 0) {
      return customerInstance;
    }

    // If both have accounts (shouldn't happen in normal flow) or neither have accounts,
    // fall back to currentAuthType preference
    if (currentAuthType === 'partner') {
      return partnerInstance;
    }
    return customerInstance;
  }, [customerInstance, partnerInstance, currentAuthType]);

  const clearState = useCallback(() => {
    console.log('🧹 Clearing state and session storage');
    sessionStorage.removeItem('user');
    deleteHeaders(['Authorization', 'X-ID-Token']);
    setUser(null);

    // Log current account state for debugging
    const customerAccounts = customerInstance.getAllAccounts();
    const partnerAccounts = partnerInstance.getAllAccounts();

    console.log(
      '🧹 Customer accounts after state clear:',
      customerAccounts.length
    );
    console.log(
      '🧹 Partner accounts after state clear:',
      partnerAccounts.length
    );

    // Note: MSAL accounts are cleared automatically on logout
    // This logging helps us understand when accounts persist unexpectedly
  }, [setUser, customerInstance, partnerInstance]);

  // Login methods
  // STEP 2A: Customer login flow initiation
  const loginAsCustomer = useCallback(async () => {
    try {
      // Check if interaction is already in progress
      const inProgress = customerInstance.getActiveAccount() !== null;
      if (inProgress) {
        return;
      }

      // STEP 3A: Redirecting to Azure AD for customer authentication
      await customerInstance.loginRedirect();
    } catch (error) {
      console.error('Customer login error:', error);
      if (
        error instanceof Error &&
        error.message?.includes('interaction_in_progress')
      ) {
        try {
          await customerInstance.handleRedirectPromise();
        } catch (redirectError) {
          console.error('Redirect handling error:', redirectError);
        }
      }
    }
  }, [customerInstance]);

  // STEP 2B: Partner login flow initiation
  const loginAsPartner = useCallback(async () => {
    try {
      // Check if interaction is already in progress
      const inProgress = partnerInstance.getActiveAccount() !== null;
      if (inProgress) {
        return;
      }

      // STEP 3B: Redirecting to Azure AD for partner authentication
      await partnerInstance.loginRedirect();
    } catch (error) {
      console.error('Partner login error:', error);
      if (
        error instanceof Error &&
        error.message?.includes('interaction_in_progress')
      ) {
        try {
          await partnerInstance.handleRedirectPromise();
        } catch (redirectError) {
          console.error('Redirect handling error:', redirectError);
        }
      }
    }
  }, [partnerInstance]);

  const logout = useCallback(() => {
    const isAuthenticated = checkAuthentication();
    const customerAccounts = customerInstance.getAllAccounts();
    const partnerAccounts = partnerInstance.getAllAccounts();

    if (isAuthenticated && user) {
      // Use the user state to determine which instance to logout from
      // This is more reliable than checking account counts
      let instanceToUse: PublicClientApplication;
      let instanceType: string;

      if (user.AffiliateId) {
        // User is a partner/affiliate - use partner instance
        instanceToUse = partnerInstance;
        instanceType = 'PARTNER';
      } else if (user.CustomerId) {
        // User is a customer - use customer instance
        instanceToUse = customerInstance;
        instanceType = 'CUSTOMER';
      } else {
        // Fallback to getCurrentInstance if user state is unclear
        instanceToUse = getCurrentInstance();
        instanceType =
          instanceToUse === customerInstance ? 'CUSTOMER' : 'PARTNER';
      }

      instanceToUse.logoutRedirect();
    } else {
      navigate(myAccountConfig.logoutRedirectUrl);
      clearState();
    }
  }, [
    checkAuthentication,
    getCurrentInstance,
    clearState,
    currentAuthType,
    customerInstance,
    partnerInstance,
    user,
  ]);

  // Auto-detect auth type whenever accounts change
  useEffect(() => {
    const customerAccounts = customerInstance.getAllAccounts();
    const partnerAccounts = partnerInstance.getAllAccounts();

    // Update auth type based on which instance has accounts
    if (partnerAccounts.length > 0 && customerAccounts.length === 0) {
      setCurrentAuthType('partner');
    } else if (customerAccounts.length > 0 && partnerAccounts.length === 0) {
      setCurrentAuthType('customer');
    }
    // If both have accounts or neither have accounts, keep current type
  }, [customerInstance, partnerInstance]);

  // Update authentication state when user or instances change
  useEffect(() => {
    const customerAccounts = customerInstance.getAllAccounts();
    const partnerAccounts = partnerInstance.getAllAccounts();
    const isAuthenticated =
      customerAccounts.length > 0 || partnerAccounts.length > 0;

    if (user && isAuthenticated) {
      setIsUserAuthenticated(true);
    } else {
      setIsUserAuthenticated(false);
    }
  }, [user, customerInstance, partnerInstance]);

  // Setup MSAL event callbacks for both instances
  useEffect(() => {
    /**
     * ==========================================
     * AUTHENTICATION FLOW - STEP BY STEP GUIDE
     * ==========================================
     *
     * This effect sets up the complete authentication flow from initial login
     * through to user data fetching. Here's the complete journey:
     *
     * STEP 1: User clicks login button
     * STEP 2: loginAsCustomer() or loginAsPartner() is called
     * STEP 3: MSAL redirects to Azure AD login
     * STEP 4: User authenticates with Azure AD
     * STEP 5: Azure AD redirects back to our app
     * STEP 6: handleRedirects() processes the redirect response
     * STEP 7: MSAL fires LOGIN_SUCCESS event (handled by handleAuthEvent below)
     * STEP 8: We extract user data from the token claims
     * STEP 9: setUser() is called with the extracted data
     * STEP 10: navigate() redirects user to dashboard
     * STEP 11: UserDataManager detects user change and starts data fetching
     * STEP 12: Customer/Affiliate data is fetched and merged
     * STEP 13: Navigation is updated based on user type
     * STEP 14: Dashboard renders with complete user context
     */

    const handleAuthEvent = (message: any) => {
      if (message.eventType === EventType.LOGIN_SUCCESS) {
        // STEP 7: LOGIN_SUCCESS event received from MSAL

        // STEP 8: Extract user identification data from JWT token claims
        const customerId = message?.payload?.idTokenClaims?.PAWSContactId;
        const affiliateId = message?.payload?.idTokenClaims?.AffiliateId;
        const userAccessToken = message?.payload?.accessToken;
        const userIdToken = message?.payload?.idToken;

        // STEP 9: Create User object and trigger data fetching chain
        if (affiliateId) {
          // STEP 9A: Setting affiliate user - this will trigger affiliate data fetching
          setUser({
            AffiliateId: affiliateId,
            UserAccessToken: userAccessToken,
            UserIdToken: userIdToken,
          });
        } else if (customerId) {
          // STEP 9B: Setting customer user - this will trigger customer data fetching
          setUser({
            CustomerId: customerId,
            UserAccessToken: userAccessToken,
            UserIdToken: userIdToken,
          });
        }

        // STEP 10: Navigate to dashboard (data fetching happens in background)
        // UserDataManager will now take over the process
        navigate(myAccountLinks.dashboard);
      }
      if (message.eventType === EventType.LOGOUT_SUCCESS) {
        // LOGOUT_SUCCESS: Cleaning up and redirecting to appropriate login page
        clearState();

        // Both customer and partner use the same login page
        navigate(myAccountLinks.login);
      }
    };

    // STEP 6: Handle any pending redirects on both instances
    // This processes the return from Azure AD after authentication
    const handleRedirects = async () => {
      try {
        await customerInstance.handleRedirectPromise();
        await partnerInstance.handleRedirectPromise();
      } catch (error) {
        // No pending redirects or processing complete
      }
    };

    handleRedirects();

    // Register event callbacks for both instances to catch auth events
    const customerCallbackId =
      customerInstance.addEventCallback(handleAuthEvent);
    const partnerCallbackId = partnerInstance.addEventCallback(handleAuthEvent);

    return () => {
      if (customerCallbackId) {
        customerInstance.removeEventCallback(customerCallbackId);
      }
      if (partnerCallbackId) {
        partnerInstance.removeEventCallback(partnerCallbackId);
      }
    };
  }, [customerInstance, partnerInstance, setUser, clearState]);

  return {
    currentAuthType,
    isUserAuthenticated,
    loginAsCustomer,
    loginAsPartner,
    logout,
    clearState,
    getCurrentInstance,
    checkAuthentication,
  };
};
